import apiClient from '../apiClient';

/**
 * Transform backend CustomerDTO to frontend customer model
 */
const transformBackendToFrontend = (backendCustomer) => {
    return {
        id: backendCustomer.publicId,
        publicId: backendCustomer.publicId,
        customerId: backendCustomer.customerId,
        categoryId: backendCustomer.categoryPublicId,
        categoryName: backendCustomer.categoryName,
        name: backendCustomer.customerName,
        whatsappNumber: backendCustomer.whatsappNumber,
        contactPerson: backendCustomer.contactPerson,
        designation: backendCustomer.designation,
        mobileNumber: backendCustomer.mobileNumber,
        email: backendCustomer.email,
        address: backendCustomer.address,
        countryId: backendCustomer.countryPublicId,
        countryName: backendCustomer.countryName,
        stateId: backendCustomer.statePublicId,
        stateName: backendCustomer.stateName,
        assignedSalesPersons: backendCustomer.assignedSalesPersons || [],
        delegatedSalesPersons: backendCustomer.delegatedSalesPersons || [],
        trn: backendCustomer.trn,
        active: backendCustomer.active,
        priceType: backendCustomer.priceType || 'RETAIL',
        createdAt: backendCustomer.createdAt
    };
};

/**
 * Transform frontend customer model to backend CustomerDTO
 */
const transformFrontendToBackend = (frontendCustomer) => {
    return {
        publicId: frontendCustomer.id,
        customerId: frontendCustomer.customerId,
        customerName: frontendCustomer.name,
        categoryPublicId: frontendCustomer.categoryId,
        whatsappNumber: frontendCustomer.whatsappNumber,
        contactPerson: frontendCustomer.contactPerson,
        designation: frontendCustomer.designation,
        mobileNumber: frontendCustomer.mobile,
        email: frontendCustomer.email,
        trn: frontendCustomer.trn,
        address: frontendCustomer.address,
        countryPublicId: frontendCustomer.countryId,
        statePublicId: frontendCustomer.stateId,
        salesPersonPublicIds: frontendCustomer.salesPersonIds || []
    };
};

/**
 * Transform frontend customer model to backend CreateCustomerRequest
 */
export const transformFrontendToCreateRequest = (frontendCustomer) => {
    return {
        customerId: frontendCustomer.customerId,
        categoryPublicId: frontendCustomer.categoryId,
        customerName: frontendCustomer.name,
        whatsappNumber: frontendCustomer.whatsappNumber,
        contactPerson: frontendCustomer.contactPerson,
        designation: frontendCustomer.designation,
        mobileNumber: frontendCustomer.mobileNumber,
        email: frontendCustomer.email,
        trn: frontendCustomer.trn,
        address: frontendCustomer.address,
        countryPublicId: frontendCustomer.countryId,
        statePublicId: frontendCustomer.stateId,
        assignedSalesPersonPublicIds: frontendCustomer.assignedSalesPersonPublicIds || [],
        delegatedSalesPersonPublicIds: frontendCustomer.delegatedSalesPersonPublicIds || [],
        priceType: frontendCustomer.priceType || 'RETAIL'
    };
};

/**
 * Transform frontend customer model to backend UpdateCustomerRequest
 */
export const transformFrontendToUpdateRequest = (frontendCustomer) => {
    return {
        customerId: frontendCustomer.customerId,
        categoryPublicId: frontendCustomer.categoryId,
        customerName: frontendCustomer.name,
        whatsappNumber: frontendCustomer.whatsappNumber,
        contactPerson: frontendCustomer.contactPerson,
        designation: frontendCustomer.designation,
        mobileNumber: frontendCustomer.mobileNumber,
        email: frontendCustomer.email,
        trn: frontendCustomer.trn,
        address: frontendCustomer.address,
        countryPublicId: frontendCustomer.countryId,
        statePublicId: frontendCustomer.stateId,
        assignedSalesPersonPublicIds: frontendCustomer.assignedSalesPersonPublicIds || [],
        delegatedSalesPersonPublicIds: frontendCustomer.delegatedSalesPersonPublicIds || [],
        priceType: frontendCustomer.priceType || 'RETAIL'
    };
};

/**
 * Get all customers for the current tenant (handles search and pagination)
 */
export const getAllCustomers = async (params = {}) => {
    try {
        const { search = '', page = 0, size = 10, status = 'ACTIVE', sort = 'createdAt,desc', salesPersonId, salesPersonRole } = params;
        
        const endpoint = search ? '/customers/search' : '/customers';
        const queryParams = {
            status,
            size,
            page,
            sort,
            ...(search && { query: search }),
            ...(salesPersonId && { salesPersonId }),
            ...(salesPersonRole && { salesPersonRole })
        };

        console.log(`[CustomerAPI] Fetching customers (search=${search}, status=${status})...`);
        const response = await apiClient.get(endpoint, { params: queryParams });

        return {
            ...response.data,
            content: (response.data.content || response.data).map(transformBackendToFrontend)
        };
    } catch (error) {
        console.error('[CustomerAPI] Error fetching customers:', error);
        throw error;
    }
};

/**
 * Search customers by query string
 * Returns only the content array
 */
export const searchCustomers = async (query) => {
    const results = await getAllCustomers({ search: query, size: 50 });
    return results.content;
};

/**
 * Get a single customer by publicId
 */
export const getCustomerById = async (publicId) => {
    try {
        const response = await apiClient.get(`/customers/${publicId}`);

        return transformBackendToFrontend(response.data);
    } catch (error) {
        console.error('Error fetching customer:', error);
        throw error;
    }
};

/**
 * Create a new customer
 */
export const createCustomer = async (customerData) => {
    try {
        const requestData = transformFrontendToCreateRequest(customerData);

        const response = await apiClient.post('/customers', requestData);

        // Response contains publicId and message
        return response.data;
    } catch (error) {
        console.error('Error creating customer:', error);
        throw error;
    }
};

/**
 * Update an existing customer
 */
export const updateCustomer = async (publicId, customerData) => {
    try {
        const requestData = transformFrontendToUpdateRequest(customerData);

        const response = await apiClient.put(`/customers/${publicId}`, requestData);

        return response.data;
    } catch (error) {
        console.error('Error updating customer:', error);
        throw error;
    }
};

/**
 * Delete a customer (Hard Delete - use with caution, usually toggleStatus is preferred)
 */
export const deleteCustomer = async (publicId) => {
    try {
        const response = await apiClient.delete(`/customers/${publicId}`);

        return response.data;
    } catch (error) {
        console.error('Error deleting customer:', error);
        throw error;
    }
};

/**
 * Toggle customer active status
 */
export const toggleCustomerStatus = async (publicId, active) => {
    try {
        const response = await apiClient.patch(`/customers/${publicId}/status`, null, { params: { active } });
        return response.data;
    } catch (error) {
        console.error('Error toggling customer status:', error);
        throw error;
    }
};

/**
 * Bulk update customers' category and/or salesperson
 */
export const bulkUpdateCustomers = async ({ customerPublicIds, categoryPublicId, salesPersonPublicId, delegatedSalesPersonPublicId }) => {
    try {
        console.log('[CustomerAPI] Bulk updating customers:', customerPublicIds.length);
        const response = await apiClient.put('/customers/bulk-update', {
            customerPublicIds,
            categoryPublicId,
            salesPersonPublicId,
            delegatedSalesPersonPublicId
        });
        console.log('[CustomerAPI] Customers bulk updated:', response.data);
        return response.data;
    } catch (error) {
        console.error('[CustomerAPI] Error bulk updating customers:', error);
        throw error;
    }
};

/**
 * Bulk import customers from Excel data
 */
export const bulkImportCustomers = async (customers) => {
    try {
        console.log('[CustomerAPI] Bulk importing customers:', customers.length);
        const response = await apiClient.post('/customers/import', { customers });
        return response.data;
    } catch (error) {
        console.error('[CustomerAPI] Error bulk importing customers:', error);
        throw error;
    }
};

/**
 * Handle API errors and return user-friendly messages
 */
export const handleApiError = (error) => {
    if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
            case 400:
                return data.message || 'Invalid data. Please check your input.';
            case 401:
                return 'Session expired. Please login again.';
            case 403:
                return 'You do not have permission to perform this action.';
            case 404:
                return 'Customer not found.';
            case 500:
                return 'Server error. Please try again later.';
            default:
                return data.message || 'An error occurred. Please try again.';
        }
    } else if (error.request) {
        // Request made but no response
        return 'Network error. Please check your connection.';
    } else {
        // Error in request setup
        return error.message || 'An unexpected error occurred.';
    }
};
/**
 * Get authorized portal access list for a customer
 */
export const getCustomerAccess = async (customerPublicId) => {
    try {
        const response = await apiClient.get(`/customers/${customerPublicId}/access`);
        return response.data;
    } catch (error) {
        console.error('[CustomerAPI] Error fetching access list:', error);
        throw error;
    }
};

/**
 * Add portal access for a customer
 */
export const addCustomerAccess = async (customerPublicId, accessData) => {
    try {
        const response = await apiClient.post(`/customers/${customerPublicId}/access`, accessData);
        return response.data;
    } catch (error) {
        console.error('[CustomerAPI] Error adding portal access:', error);
        throw error;
    }
};

/**
 * Remove portal access for a customer
 */
export const removeCustomerAccess = async (customerPublicId, accessPublicId) => {
    try {
        const response = await apiClient.delete(`/customers/${customerPublicId}/access/${accessPublicId}`);
        return response.data;
    } catch (error) {
        console.error('[CustomerAPI] Error removing portal access:', error);
        throw error;
    }
};

/**
 * Fetch pending credit invoices for a customer
 */
export const getCustomerPendingInvoices = async (customerId) => {
    try {
        const response = await apiClient.get(`/customers/${customerId}/pending-invoices`);
        return response.data;
    } catch (error) {
        console.error('[CustomerAPI] Error fetching pending invoices:', error);
        throw error;
    }
};

/**
 * Merge one or more duplicate customers into a primary target customer
 */
export const mergeCustomers = async ({ targetCustomerPublicId, duplicateCustomerPublicIds }) => {
    try {
        const response = await apiClient.post('/customers/merge', {
            targetCustomerPublicId,
            duplicateCustomerPublicIds
        });
        return response.data;
    } catch (error) {
        console.error('[CustomerAPI] Error merging customers:', error);
        throw error;
    }
};

export const customerApi = {
    getAllCustomers,
    searchCustomers,
    getCustomerById,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    toggleCustomerStatus,
    bulkUpdateCustomers,
    bulkImportCustomers,
    getCustomerAccess,
    addCustomerAccess,
    removeCustomerAccess,
    getCustomerPendingInvoices,
    mergeCustomers,
    handleApiError
};

export default customerApi;
